/**
 * Access token payload shape (Keycloak JWT).
 * resource_access: { [clientId]: { roles: string[] } }
 */
export interface AccessTokenPayload {
  resource_access?: Record<string, { roles?: string[] }>;
  [key: string]: unknown;
}

const HUB_BACK_CLIENT = 'hub-back';

/**
 * Decodes a JWT payload (middle part, base64url) without verifying signature.
 * Used only to read roles from an already-validated token (Keycloak validates it).
 */
export function parseAccessTokenPayload(token: string): AccessTokenPayload | null {
  if (!token?.trim()) return null;
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payloadBase64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(
      atob(payloadBase64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(json) as AccessTokenPayload;
  } catch {
    return null;
  }
}

/**
 * Returns true if the token payload has at least one role for hub-back
 * (e.g. hub-user, hub-admin). Client id lookup is case-insensitive.
 */
export function hasHubRoleFromPayload(payload: AccessTokenPayload | null): boolean {
  if (!payload?.resource_access) return false;
  const key = Object.keys(payload.resource_access).find(
    k => k.toLowerCase() === HUB_BACK_CLIENT.toLowerCase()
  );
  const roles = key ? payload.resource_access[key]?.roles : undefined;
  return Array.isArray(roles) && roles.length > 0;
}
